import { DatePicker, Input, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import "./FilterPanel.css";

/**
 * Reusable filter panel: title "Filters", close (X), optional search, optional date range, dropdowns, optional Clear All.
 * @param {boolean} open - whether panel is visible
 * @param {function} onClose - when X or outside is clicked
 * @param {object} search - { placeholder, value, onChange } (omit or placeholder undefined to hide)
 * @param {array} filters - [{ key, label, placeholder, value, onChange, options }]
 * @param {array} dateRange - [start, end] dayjs or null
 * @param {function} onDateRangeChange - (dates) => {}
 * @param {function} onClearAll - optional, shows "Clear All Filters" button
 */
const FilterPanel = ({
	open,
	onClose,
	search = {},
	filters = [],
	dateRange,
	onDateRangeChange,
	onClearAll,
}) => {
	if (!open) return null;

	return (
		<>
			<div className="filter-panel-backdrop" onClick={onClose} aria-hidden="true" />
			<div className="filter-panel">
				<div className="filter-panel-header">
					<span className="filter-panel-title">Filters</span>
					<button type="button" className="filter-panel-close" onClick={onClose} aria-label="Close">
						<CloseOutlined />
					</button>
				</div>
				<div className="filter-panel-body">
					{onDateRangeChange != null && (
						<>
							<div className="filter-panel-field">
								<label className="filter-panel-label">Start Date</label>
								<DatePicker
									className="filter-panel-input"
									format="DD-MM-YYYY"
									placeholder="dd-mm-yyyy"
									value={Array.isArray(dateRange) ? dateRange[0] ?? null : null}
									onChange={(d) => {
										const end = Array.isArray(dateRange) ? dateRange[1] : null;
										onDateRangeChange(d != null ? [d, end ?? d] : (end != null ? [null, end] : null));
									}}
									allowClear
									style={{ width: "100%" }}
								/>
							</div>
							<div className="filter-panel-field">
								<label className="filter-panel-label">End Date</label>
								<DatePicker
									className="filter-panel-input"
									format="DD-MM-YYYY"
									placeholder="dd-mm-yyyy"
									value={Array.isArray(dateRange) ? dateRange[1] ?? null : null}
									onChange={(d) => {
										const start = Array.isArray(dateRange) ? dateRange[0] : null;
										onDateRangeChange(d != null ? [start ?? d, d] : (start != null ? [start, null] : null));
									}}
									allowClear
									style={{ width: "100%" }}
								/>
							</div>
						</>
					)}
					{search.placeholder != null && (
						<div className="filter-panel-field">
							<label className="filter-panel-label">Search</label>
							<Input
								placeholder={search.placeholder}
								value={search.value ?? ""}
								onChange={(e) => search.onChange?.(e.target.value)}
								allowClear
								className="filter-panel-input"
							/>
						</div>
					)}
					{filters.map((f) => (
						<div key={f.key} className="filter-panel-field">
							<label className="filter-panel-label">{f.label}</label>
							<Select
								placeholder={f.placeholder}
								value={f.value}
								onChange={f.onChange}
								mode={f.mode}
								allowClear
								className="filter-panel-select"
								options={f.options}
								showSearch={f.showSearch}
								maxTagCount={f.mode === "multiple" ? "responsive" : undefined}
								filterOption={f.filterOption ?? (f.showSearch ? (input, opt) => (opt?.label ?? "").toString().toLowerCase().includes((input || "").toLowerCase()) : undefined)}
								getPopupContainer={(node) => node?.parentNode ?? document.body}
							/>
						</div>
					))}
					{typeof onClearAll === "function" && (
						<div className="filter-panel-field" style={{ marginTop: 8 }}>
							<button
								type="button"
								className="filter-panel-clear-all"
								onClick={() => { onClearAll(); onClose?.(); }}
							>
								Clear All Filters
							</button>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default FilterPanel;
